import React, { Component, PropsWithChildren, RefObject } from 'react';

import { getFocusableElements } from '../../../lib/DomUtils/utils';

/**
 * Deprecated
 * - not encouraged to use this anymore
 *
 * Handles disappearing Focus inside its children.
 *
 * When to use:
 *  - Components dynamically diss/-appearing, e.g deleteable lists
 *
 * How To use:
 * - Wrap <PreventLostFocus>  around a Child
 *   -> parent component needs to rerender, so componentDidUpdate is called.
 *
 * Whats happening:
 *  - using getSnapshotBeforeUpdate to capture last focused element.
 *  - last focused element !== same after update -> focus last possible element.
 *
 * What needs to be done:
 *  - Currently DomNodeUtility returns all possible focusable elements, e.g also Skiplinks.
 *     -> property in order to evaluate different sets of focusable elements ?
 *     -> property to not focus skiplinks and other focusable elements we dont want to automatically focus?
 */

type PreventLostFocusProps = {
  attachTo: RefObject<any | null>;
};

type FocusSnapshot = {
  lastFocusedElement: Element | null;
  lastFocusElementIndex: number;
};

export default class PreventLostFocus extends Component<PropsWithChildren<PreventLostFocusProps>> {
  domNode: HTMLElement | null = null;

  componentDidMount() {
    this.domNode = this.props.attachTo.current;
  }

  /**
   * Called before anything is committed to DOM.
   *  -> we can capture where focus was last (before possible disappearance)
   */
  getSnapshotBeforeUpdate(): FocusSnapshot | null {
    if (this.domNode) {
      const activeElement = this.getActiveElement();
      const focusableElements = getFocusableElements(this.domNode, { dontFocusSkipLinks: true });

      return {
        lastFocusedElement: activeElement,
        lastFocusElementIndex: focusableElements.findIndex(el => el === activeElement),
      };
    }
    return null;
  }

  componentDidUpdate(
    _prevProps: any,
    _prevState: any,
    snapshot: FocusSnapshot | null,
  ) {
    this.focusPossibleElement(snapshot);
  }

  /**
   * Check if the current focus is on the same Element as the last focus.
   * either:
   *  -> focus new element in with 'lastFocusElementIndex' so we keep focus order in tact.
   *  -> or if not found, just focus the last possible element.
   */
  focusPossibleElement(snapshot: FocusSnapshot | null) {
    if (this.domNode && snapshot !== null) {
      if (this.getActiveElement() !== snapshot.lastFocusedElement) {
        const focusables = getFocusableElements(this.domNode, { dontFocusSkipLinks: true });
        const elementToFocus = focusables[snapshot.lastFocusElementIndex]
          ? focusables[snapshot.lastFocusElementIndex]
          : focusables[focusables.length - 1];
        elementToFocus?.focus();
      }
    }
  }

  getActiveElement(): Element | null {
    return document.activeElement;
  }

  render() {
    return <>{this.props.children}</>;
  }
}
